import {
  BLANK_SLATE_DOCUMENT,
  BLANK_SLATE_DOCUMENT_WITH_LEFT_ALIGNMENT,
  isEmptySlateDocument,
} from "../core/constants";
import { SiteFeatureFlag } from "../resources/modelTypes";
import {
  AccommodationDetails,
  AnchorDetails,
  AttendantDetails,
  CashRegistryDetails,
  CommentsDetails,
  DisplayMode,
  DividerDetails,
  FAQDetails,
  GalleryDetails,
  ImageAndTextDetails,
  LocationDetails,
  RegistryDetails,
  SectionDetailsType,
  SectionTypes,
  Site,
  StripeRegistryDetails,
  TestValues,
  TextDetails,
} from "../resources/modelTypes";
import { attendantTitles } from "../wedding-party/constants";
import { PageSlugs } from "./types";
import { useCallback, useEffect, useRef, useState } from "react";

export const isUsingNewComponents = (site: Site): boolean =>
  site.featureFlags[SiteFeatureFlag.SectionEditorExperience] ===
  TestValues.Experiment;

export const getDefaultSectionDetailsByType = (
  sectionType: SectionTypes,
): SectionDetailsType => {
  switch (sectionType) {
    case SectionTypes.Text:
      return {
        content: BLANK_SLATE_DOCUMENT,
        title: "",
      } as TextDetails;
    case SectionTypes.Gallery:
      return {
        content: BLANK_SLATE_DOCUMENT,
        galleryMode: "inline",
        title: "",
      } as GalleryDetails;
    case SectionTypes.ImageAndText:
      return {
        content: BLANK_SLATE_DOCUMENT,
        displayMode: DisplayMode.Bottom,
        title: "",
      } as ImageAndTextDetails;
    case SectionTypes.Anchor:
      return {
        anchorText: "",
        websiteUrl: "",
      } as AnchorDetails;
    case SectionTypes.Registry:
      return {
        buttonText: null,
        content: BLANK_SLATE_DOCUMENT_WITH_LEFT_ALIGNMENT,
        hideDefaultRegistryImage: false,
        title: "",
        websiteUrl: "",
      } as RegistryDetails;
    case SectionTypes.CashRegistry:
      return { hideDefaultRegistryImage: false } as CashRegistryDetails;
    case SectionTypes.StripeRegistry:
      return {
        giftAmountCents: 0,
        goalAmountCents: 0,
        hideGoal: false,
        isFixedAmount: false,
        isVisible: true,
        showQuickOptions: false,
        textDescription: null,
        title: "",
      } as StripeRegistryDetails;
    case SectionTypes.Location:
      return {
        address1: "",
        address2: "",
        city: "",
        content: BLANK_SLATE_DOCUMENT,
        country: "",
        hasPhysicalAddress: true,
        state: "",
        title: "",
        venue: "",
        zipCode: "",
      } as LocationDetails;
    case SectionTypes.Divider:
      return {} as DividerDetails;
    case SectionTypes.Comments:
      return {} as CommentsDetails;
    case SectionTypes.Attendant:
      return {
        content: BLANK_SLATE_DOCUMENT,
        displayMode: DisplayMode.Top,
        name: "",
        photo: "",
        position: "",
        title: attendantTitles[0].value,
      } as AttendantDetails;
    case SectionTypes.Accommodation:
      return {
        anchorText: null,
        content: BLANK_SLATE_DOCUMENT,
        title: "",
        websiteUrl: "",
      } as AccommodationDetails;
    case SectionTypes.FAQ:
      return {
        answer: BLANK_SLATE_DOCUMENT,
        question: "",
      } as FAQDetails;
    default:
      return {};
  }
};

export const SECTION_DISPLAY_NAME: Record<string, string> = {
  [SectionTypes.Attendant]: "person",
  [SectionTypes.Accommodation]: "travel",
};

export const useResizeObserverWidth = (): {
  ref: (node: HTMLDivElement | null) => void;
  width: number | null;
} => {
  const [width, setWidth] = useState<number | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (node) {
      const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        const entry = entries[0];

        if (entry) {
          setWidth(entry.contentRect.width);
        }
      });

      observer.observe(node);
      observerRef.current = observer;

      setWidth(node.getBoundingClientRect().width);
    }
  }, []);

  useEffect(
    () => () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    },
    [],
  );

  return {
    ref,
    width,
  };
};

export const useStickyState = (): {
  isStuck: boolean;
  sentinelRef: (node: HTMLDivElement | null) => void;
} => {
  const [isStuck, setIsStuck] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Ref callback pattern so we know when the node mounts/unmounts
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    // Cleanup previous observer if it exists
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (node) {
      const observer = new IntersectionObserver(
        ([entry]) => setIsStuck(!entry.isIntersecting),
        {
          threshold: 0,
        },
      );

      observer.observe(node);
      observerRef.current = observer;
    }
  }, []);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    },
    [],
  );

  return {
    isStuck,
    sentinelRef,
  };
};

export const slugToPrimarySectionType = {
  [PageSlugs.Accommodations]: SectionTypes.Accommodation,
  [PageSlugs.Photos]: SectionTypes.Gallery,
  [PageSlugs.WeddingParty]: SectionTypes.Attendant,
  [PageSlugs.Events]: SectionTypes.Event__FAKE,
  [PageSlugs.Registry]: SectionTypes.Registry,
  [PageSlugs.FAQ]: SectionTypes.FAQ,
};

export const allSlugs: Set<string> = new Set(Object.values(PageSlugs));

export const isEventDetailsEmpty = (event) => {
  const noTime = !event.time;
  const noLocation =
    !event.address1 && !event.city && !event.state && !event.country;
  const noDescription = isEmptySlateDocument(event.description);
  const noQuestions = !event.questions?.length;

  return noTime && noLocation && noDescription && noQuestions;
};
